import { useEffect, useState, useRef } from 'react';

// third party utils
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// iangels utils
import { MINUTE_IN_MILLISECOND } from '../helpers/constants';
import { track, identify } from '../services/analytics';
import { loadConfig } from '../env/config_util';

import useInterval from '../components/useInterval';
import { useAuth0 } from '@auth0/auth0-react';

import {
  getIdentifyUserFailureSelector,
  getAuthAttemptingSelector,
  getUserAttemptingSelector,
  getAuthResultSelector,
  getTraitsSelector,
  getUserSelector,
  getForceUserInfoSelector,
} from '../containers/App/selectors';
import {
  clearIdentifyFailureState,
  identifyUserAttempt,
  setUserSuccess,
  setAuthResult,
  clearState,
  setForceUserInfo,
} from '../containers/App/actions';
import useCheckAuthentication from '../hooks/useCheckAuthentication';
import useGetUser from '../hooks/useGetUser';

import {getIsImpersonatedSelector, getImpersonatedUserSelector } from '../containers/Impersonate/selectors';
import {setImpersonated } from '../containers/Impersonate/actions';
import {
  claimRedirect,
  decideLegacyAccess,
  isGateEnabled,
  isLeavingLegacy,
  staffRulesFromConfig,
} from './staffAccess';
import StaffOnlyNotice from './StaffOnlyNotice';

const RoutGuard = ({ hasNext, navigateTo, lockGuard ,isSignup}) => {
  const { loginWithRedirect, isAuthenticated, isLoading, error, getIdTokenClaims, logout, user: auth0User } = useAuth0();
  const user = useGetUser();
  const config = loadConfig();
  // state
  const [isRunning, setIsRunning] = useState(true);
  const [staffGateBlocked, setStaffGateBlocked] = useState(false);
  
  // selectors
  const identifyUserFailure = useSelector(getIdentifyUserFailureSelector);
  const forceUserInfo = useSelector(getForceUserInfoSelector);
  const authLoading = useSelector(getAuthAttemptingSelector);
  const userLoading = useSelector(getUserAttemptingSelector);
  const authResult = useSelector(getAuthResultSelector);
  const traits = useSelector(getTraitsSelector);
  const userState = useSelector(getUserSelector);
  const isImpersonated = useSelector(getIsImpersonatedSelector);
  const impersonatedUser = useSelector(getImpersonatedUserSelector);

  // hooks
  const checkAuthentication = useCheckAuthentication(setIsRunning);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const hasMergedRef = useRef(false);

  useInterval(checkAuthentication, isRunning ? MINUTE_IN_MILLISECOND : null);

  // Staff-only gate after the V2 go-live (off unless this env's config sets staff_only_gate) — ./staffAccess.js.
  // The REAL signed-in account must be staff (during an impersonation: the impersonator), else it is signed out of
  // legacy locally and sent to V2's login. Runs before the effects below can start a new login.
  useEffect(() => {
    if (isLoading || isLeavingLegacy()) return;
    const verdict = decideLegacyAccess({
      enabled: isGateEnabled(config.config_module),
      pathname: window.location.pathname,
      auth0User: isAuthenticated ? auth0User : null,
      impersonatedUser,
      traits,
      impersonationHandoff: isImpersonated,
      rules: staffRulesFromConfig(config.config_module),
    });
    if (verdict !== 'deny') return;
    const goNow = claimRedirect(window.sessionStorage);
    dispatch(clearState());
    logout({ localOnly: true }); // auth0-react 1.x: clears this app's session only, no Auth0 /v2/logout round trip
    if (goNow) {
      window.location.replace(config.config_module.staff_gate_redirect_url);
    } else {
      setStaffGateBlocked(true); // just came back from V2's login: show a message instead of looping
    }
  }, [isLoading, isAuthenticated, auth0User, impersonatedUser, traits, isImpersonated, dispatch, logout, config.config_module]);

  useEffect(() => {
    if (isLeavingLegacy()) return;
    if (identifyUserFailure || error) {
      dispatch(clearIdentifyFailureState());
      if (error && error.message === 'Email is required to facebook'){ 
       logout({ logoutParams: { returnTo: '/' } });
       loginWithRedirect({ isSignUp: true, appState: { returnTo: '/' } , databaseConnection: config.config_module.auth0_connection , errorDescription: 'Something went wrong and we couldn\'t complete your Facebook registration, please try different registration options or contact backoffice@iangels.com' });
      } else if (error && error.message === 'An account with this email already exists using email and password'){ 
        const errorDescription = "An account with this email already exists using email and password. Please sign in using that method. If you would like to enable social login for this account, please contact us on backoffice@iangels.com";
        logout({ logoutParams: { returnTo: '/' } });
        loginWithRedirect({ isSignUp: true, appState: { returnTo: '/' } , databaseConnection: config.config_module.auth0_connection , errorDescription: errorDescription });
      } else {
        navigate('/error');
      } 
    }

  }, [identifyUserFailure, navigate, dispatch, error, loginWithRedirect, logout, config.config_module.auth0_connection]);

  useEffect(() => {
    if (isLeavingLegacy()) return; // the staff-only gate is signing this page out — no identity call to legacy's API
    if (!isLoading && !isAuthenticated) return;

    const getIdToken = async () => {
      try {
        const idTokenClaims = await getIdTokenClaims();
        if (!idTokenClaims) return;
        if (JSON.stringify(idTokenClaims) !== JSON.stringify(authResult)) {
          dispatch(setAuthResult(idTokenClaims));
        }
        const needsIdentify = !traits || JSON.stringify(traits) === '{}' || isImpersonated;
        if (needsIdentify) {
          dispatch(identifyUserAttempt());
        }
      } catch (err) {
        console.error('Error while getting token:', err);
      }
    };

    if (isLoading === false && isAuthenticated) {
      getIdToken();
    }
  }, [isAuthenticated, isLoading, isImpersonated]);

  useEffect(() => {
    if (isLeavingLegacy()) return; // the staff-only gate is signing this page out — never start a new login
    if (!isLoading && !isAuthenticated && !error && lockGuard && !isImpersonated) {
      dispatch(clearState());
      logout({ logoutParams: { returnTo: '/' } });
      //const isSignup = document.location.pathname.indexOf('signup') !== -1;
      loginWithRedirect({ isSignUp: isSignup, appState: { returnTo: '/' } , databaseConnection: config.config_module.auth0_connection });
    }
  }, [isLoading, isAuthenticated, error, lockGuard, loginWithRedirect,config.config_module.auth0_connection]);

  useEffect(() => {
    if (isLeavingLegacy()) return;
    if (!userState) {
      hasMergedRef.current = false;
    }
    if (!isLoading && !user && userState) {
      dispatch(clearState());
      logout({ logoutParams: { returnTo: '/' } });
      return;
    }

    const traitName = traits?.fullName || traits?.full_name;
    const shouldMerge =
      (traits && !userState && user && !authLoading && !userLoading) ||
      (forceUserInfo && user && traits);
    if (shouldMerge && user && !hasMergedRef.current) {
      hasMergedRef.current = true;
      if (forceUserInfo) {
        dispatch(setForceUserInfo(false));
      }
      const mergedUser = {
        ...user,
        user_id: authResult?.user_id || authResult?.sub || user?.user_id || user?.sub,
        full_name: traitName || user?.user_metadata?.full_name || user?.name,
        email: traits?.email || user?.email,
        name: traitName || user?.name || user?.user_metadata?.full_name,
        visits: traits?.visits ?? user?.visits,
        user_metadata: {
          ...(user?.user_metadata || {}),
          full_name: traitName || user?.user_metadata?.full_name,
          avatar: traits?.avatar || user?.user_metadata?.avatar,
        },
        app_metadata: {
          ...(user?.app_metadata || {}),
          has_details: traits?.has_details ?? user?.app_metadata?.has_details ?? authResult?.['https://iangels.com/has_details'],
          level: traits?.level ?? user?.app_metadata?.level ?? authResult?.['https://iangels.com/level'],
          roles: traits?.roles ?? user?.app_metadata?.roles ?? user?.roles ?? authResult?.['https://iangels.com/roles'],
        },
        roles: traits?.roles ?? user?.roles ?? user?.app_metadata?.roles ?? authResult?.['https://iangels.com/roles'],
        level: traits?.level ?? user?.level ?? user?.app_metadata?.level ?? authResult?.['https://iangels.com/level'],
      };
      dispatch(setUserSuccess(mergedUser));
      identify(false);
      track((traits?.visits ?? user?.visits) > 1 ? 'Sign In' : 'Sign Up', {});
    }
    if (forceUserInfo && userState) {
      hasMergedRef.current = false;
    }
  }, [traits, user, userState, userLoading, authLoading, dispatch, authResult, forceUserInfo]);

  useEffect(() => {
    const location = window.location;
    const hash = location.hash;

    if (hash?.indexOf('#access_token=') === 0) {
      navigate('/imper?' + hash.substr(1));
      dispatch(setImpersonated(true));
      return;
    }
    if (authResult) {
      if (hasNext) {
        navigate(`/${navigateTo}`);
      }
      return;
    }
    //!authResult - at start
    if(location.pathname==="/imper")
    {
        dispatch(setImpersonated(true));
        return;
    }
  }, [authResult, navigate, dispatch, navigateTo, hasNext]);

  if (staffGateBlocked) return <StaffOnlyNotice url={config.config_module.staff_gate_redirect_url} />;
  return null;
};

export default RoutGuard;
